import React, { useEffect, useState } from "react";
import { Select, Form } from "antd";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";

/**
 * Partner Location Selector - fetches cities and locations from API
 * @component
 * @alias PartnerLocationSelector
 * @property {array} initialValue - init value for select (array of 'city-19', 'loc-1')
 * @property {bool} readOnly - sets Form Item read-only
 * @property {bool} required - sets Form Item required - default false
 * @returns <Form.Item /> containing <Select />
 */
const PartnerLocationSelector = ({
 initialValue,
 required,
 readOnly,
 sendOnchange = null,
 clearable = false,
 hideLabel = false
}) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [locations, setLocations] = useState([]);
  const [selectedValues, setSelectedValues] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const fetchLocations = async () => {
    if (locations.length) return;

    setLoading(true);
    try {
      const response = await coreApi.get('/partners/available-locations');

      const groupedLocations = [];
      const cities = response.data.filter(item => item.type === 'city');
      const locs = response.data.filter(item => item.type === 'location');

      cities.forEach(city => {
        groupedLocations.push(city);

        const cityLocations = locs.filter(loc => loc.cityBranchName === city.name);
        groupedLocations.push(...cityLocations);
      });

      const orphanLocations = locs.filter(loc => 
        !cities.some(city => city.name === loc.cityBranchName)
      );
      groupedLocations.push(...orphanLocations);
      
      setLocations(groupedLocations);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const renderLocationLabel = (location) => {
    if (location.type === 'location') {
      return location.cityBranchName 
        ? `  ↳ ${location.name}` 
        : location.name;
    }
    return location.name;
  };

  const getCityLocations = (cityName) => {
    return locations
      .filter(loc => loc.type === 'location' && loc.cityBranchName === cityName)
      .map(loc => loc.value);
  };

  const handleSelectionChange = (values) => {
    const previousValues = selectedValues || [];
    const newValues = values || [];

    const added = newValues.filter(v => !previousValues.includes(v));
    const removed = previousValues.filter(v => !newValues.includes(v));

    let finalValues = [...newValues];

    added.forEach(value => {
      if (value.startsWith('city-')) {
        const city = locations.find(loc => loc.value === value);
        if (city) {
          const cityLocations = getCityLocations(city.name);
          cityLocations.forEach(locValue => {
            if (!finalValues.includes(locValue)) {
              finalValues.push(locValue);
            }
          });
        }
      }
    });

    removed.forEach(value => {
      if (value.startsWith('city-')) {
        const city = locations.find(loc => loc.value === value);
        if (city) {
          const cityLocations = getCityLocations(city.name);
          finalValues = finalValues.filter(v => !cityLocations.includes(v));
        }
      }
    });

    removed.forEach(value => {
      if (value.startsWith('loc-')) {
        const location = locations.find(loc => loc.value === value);
        if (location && location.cityBranchName) {
          const city = locations.find(loc => 
            loc.type === 'city' && loc.name === location.cityBranchName
          );
          if (city) {
            const cityLocations = getCityLocations(city.name);
            const hasOtherLocations = cityLocations.some(locValue => 
              finalValues.includes(locValue) && locValue !== value
            );
            if (!hasOtherLocations && finalValues.includes(city.value)) {
              finalValues = finalValues.filter(v => v !== city.value);
            }
          }
        }
      }
    });

    setSelectedValues(finalValues);
    
    if (form) {
      form.setFieldValue('preferred_location', finalValues);
    }
    
    if (!isFocused && sendOnchange) {
      sendOnchange(finalValues);
    }
  };

  return (
    <Form.Item
      name="preferred_location"
      label={hideLabel ? undefined : t("partners.selectPartnerLocation")}
      rules={[
        {
          required: required,
          message: t("partners.validation.state"),
        },
      ]}
      initialValue={initialValue}
      style={hideLabel ? {width: 'auto'} : {minWidth: '400px'}}
    >
      <Select
        disabled={readOnly}
        value={selectedValues}
        allowClear={clearable}
        loading={loading}
        onFocus={() => setIsFocused(true)}
        onChange={handleSelectionChange}
        onBlur={() => {
          setIsFocused(false);
          if (sendOnchange) sendOnchange(selectedValues);
        }}
        showSearch={true}
        filterOption={(input, option) => {
          const searchText = input.toLowerCase();
          const optionText = option.children.toLowerCase();
          return optionText.indexOf(searchText) >= 0;
        }}
        mode="multiple"
      >
        {locations.map((location) => (
          <Select.Option 
            key={location.value} 
            value={location.value}
            style={location.type === 'location' ? { paddingLeft: '24px' } : {}}
          >
            {renderLocationLabel(location)}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default PartnerLocationSelector;
